import React from "react";
import {
  Box,
  Typography,
  Paper,
  Icon,
  Chip,
  alpha,
  Skeleton,
  Button,
  Pagination,
} from "@mui/material";
import { RFQActivityLog } from "../../types/rfq.types";

interface RFQActivityTimelineProps {
  activities: RFQActivityLog[];
  loading?: boolean;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  showPagination?: boolean;
}

// Activity configuration for RFQ-specific icons, colors, and labels
const rfqActivityConfig: Record<
  string,
  {
    icon: string;
    color: string;
    bgColor: string;
    label: string;
    description: string;
  }
> = {
  created: {
    icon: "add_circle",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    label: "Created",
    description: "RFQ was created from Purchase Request",
  },
  updated: {
    icon: "edit",
    color: "#0891B2",
    bgColor: "#ECFEFF",
    label: "Updated",
    description: "RFQ details were modified",
  },
  sent: {
    icon: "send",
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    label: "Sent",
    description: "RFQ was sent to suppliers",
  },
  in_progress: {
    icon: "hourglass_empty",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    label: "In Progress",
    description: "Suppliers are preparing quotations",
  },
  completed: {
    icon: "check_circle",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Completed",
    description: "RFQ process completed with quotation acceptance",
  },
  cancelled: {
    icon: "cancel",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    label: "Cancelled",
    description: "RFQ was cancelled",
  },
  quotation_added: {
    icon: "add_business",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Quotation Added",
    description: "New vendor quotation received",
  },
  quotation_accepted: {
    icon: "task_alt",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Quotation Accepted",
    description: "Vendor quotation accepted",
  },
  quotation_rejected: {
    icon: "block",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    label: "Quotation Rejected",
    description: "Vendor quotation rejected",
  },
};

const formatTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInHours * 60);
    return diffInMinutes <= 1 ? "Just now" : `${diffInMinutes} minutes ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 168) {
    // 7 days
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};

const RFQActivityTimelineItem: React.FC<{
  activity: RFQActivityLog;
  isLast: boolean;
}> = ({ activity, isLast }) => {
  const config =
    rfqActivityConfig[activity.action] || rfqActivityConfig.updated;

  return (
    <Box sx={{ position: "relative", pb: isLast ? 0 : 3 }}>
      {/* Timeline line */}
      {!isLast && (
        <Box
          sx={{
            position: "absolute",
            left: 20,
            top: 48,
            bottom: 0,
            width: 2,
            bgcolor: "#E5E7EB",
            zIndex: 0,
          }}
        />
      )}

      <Box sx={{ display: "flex", gap: 2, position: "relative", zIndex: 1 }}>
        {/* Timeline icon */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            bgcolor: config.bgColor,
            color: config.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${config.color}`,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }}>{config.icon}</Icon>
        </Box>

        {/* Activity content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: `1px solid ${alpha(config.color, 0.2)}`,
              "&:hover": {
                boxShadow: 1,
                borderColor: alpha(config.color, 0.3),
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Chip
                  label={config.label}
                  size="small"
                  sx={{
                    bgcolor: config.bgColor,
                    color: config.color,
                    fontWeight: 600,
                    border: `1px solid ${alpha(config.color, 0.3)}`,
                  }}
                />
                {activity.previous_status && activity.new_status && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {activity.previous_status}
                    </Typography>
                    <Icon sx={{ fontSize: 14, color: "text.secondary" }}>
                      arrow_forward
                    </Icon>
                    <Typography variant="caption" color="text.secondary">
                      {activity.new_status}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {formatTimestamp(activity.timestamp)}
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                mb: activity.details ? 1 : 0,
                lineHeight: 1.5,
              }}
            >
              {config.description}
            </Typography>

            {/* Additional details */}
            {activity.details && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                  lineHeight: 1.4,
                }}
              >
                {activity.details}
              </Typography>
            )}

            {/* User information */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1.5,
                pt: 1.5,
                borderTop: `1px solid ${alpha("#000", 0.08)}`,
              }}
            >
              <Icon sx={{ fontSize: 16, color: "text.secondary" }}>person</Icon>
              <Typography variant="caption" color="text.secondary">
                {activity.changed_by_email || activity.changed_by}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const RFQActivityTimelineSkeleton: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    {[1, 2, 3].map((item) => (
      <Box key={item} sx={{ display: "flex", gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="rectangular"
            height={100}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

export const RFQActivityTimeline: React.FC<RFQActivityTimelineProps> = ({
  activities,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  showPagination = true,
}) => {
  if (loading) {
    return <RFQActivityTimelineSkeleton />;
  }

  if (activities.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          textAlign: "center",
        }}
      >
        <Icon
          sx={{
            fontSize: 48,
            color: "text.secondary",
            mb: 2,
          }}
        >
          history
        </Icon>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Activity Yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 300 }}
        >
          RFQ activity will appear here as actions are performed on this
          request.
        </Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Box>
      {/* Activity Timeline */}
      <Box sx={{ mb: showPagination && totalPages > 1 ? 3 : 0 }}>
        {activities.map((activity, index) => (
          <RFQActivityTimelineItem
            key={`${activity.timestamp}-${activity.action}-${index}`}
            activity={activity}
            isLast={index === activities.length - 1}
          />
        ))}
      </Box>

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => onPageChange?.(newPage)}
            color="primary"
            size="small"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Box>
  );
};
